import * as React from 'react'
import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import TimePicker from 'modules/core/components/Inputs/TimePicker'
import Popover from 'modules/core/components/PopoverMenu'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import AddPlannedBreakModal from 'modules/shift_management/components/Settings/SavedShifts/AddPlannedBreakModal'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import type DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'

import PlannedBreaks from './PlannedBreaks'

interface Props {
  form: DefaultShiftForm
  plannedBreakForms: PlannedBreakForm[]
  setPlannedBreakForms: React.Dispatch<React.SetStateAction<PlannedBreakForm[]>>
}

const ShiftsAndBreaksTimePickers = ({ form, plannedBreakForms, setPlannedBreakForms }: Props) => {
  const [{ canManageBreakConfigurations }] = usePermissions()
  const { startAt, endAt } = form
  const [openAddBreakModal, setOpenAddBreakModal] = useState<boolean>(false)
  const { isTimeFormat24H } = useIsTimeFormat24H()

  const onDeleteBreak = (breakForm: PlannedBreakForm) => {
    const updatedForms = plannedBreakForms.filter(
      (plannedBreakForm) => plannedBreakForm !== breakForm
    )
    setPlannedBreakForms(updatedForms)
  }

  const onAddBreak = (breakConfiguration: BreakConfigurationFragment, breakType: BreakType) => {
    const { name, id, paid } = breakConfiguration
    const newBreakForm = new PlannedBreakForm(
      {
        'break_configuration.id': id,
        'break_configuration.name': name,
        'break_configuration.paid': paid,
        break_type: breakType,
      },
      isTimeFormat24H
    )
    track(trackingEvents.shiftManagement.addBreakSubmit, {
      id,
      type: paid ? 'paid' : 'unpaid',
      scheduling_type: breakType,
      from: 'saved-shifts',
    })
    setPlannedBreakForms([...plannedBreakForms, newBreakForm])
  }

  const onClickAdd = () => {
    track(trackingEvents.shiftManagement.addBreakStart, { from: 'saved-shifts' })
    setOpenAddBreakModal(true)
  }

  return (
    <Box
      borderRadius={{ all: 'abs008' }}
      border={{
        all: {
          style: 'solid',
          width: 's1',
          color: 'grey400',
        },
      }}
      padding="s24"
      gap="s16"
    >
      <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
        <Text.Normal size="300" weight="medium" color="grey800">
          {i18n.t('shift_management.default_shift.form_labels.shift')}
        </Text.Normal>

        <Box flexDirection="row" gap="s8">
          <Button type="secondary" disabled iconName="delete" />
          <Popover type="list">
            <Popover.Trigger>
              <Button type="secondary" iconName="add" />
            </Popover.Trigger>
            <Popover.Content side="bottom" align="end" width="s200">
              <Popover.TouchableOption
                onClick={onClickAdd}
                title={i18n.t('shift_management.saved_shifts.actions.add.break')}
              />
            </Popover.Content>
          </Popover>
        </Box>
        {openAddBreakModal && (
          <AddPlannedBreakModal
            onAddBreak={onAddBreak}
            onClose={() => setOpenAddBreakModal(false)}
            from="saved-shifts"
            hideCreateBreakButton={!canManageBreakConfigurations}
          />
        )}
      </Box>

      <Box flexDirection="row" alignItems="center" gap="s16">
        <Box>
          <Box flexDirection="row" alignItems="center">
            <TimePicker
              field={form.get('start_at')}
              isTimeFormat24H={isTimeFormat24H}
              isNotValid={form.isDirty && !form.isValidTime}
              mandatory
            />
            <Box paddingX="s8">-</Box>
            <TimePicker
              field={form.get('end_at')}
              isTimeFormat24H={isTimeFormat24H}
              isNotValid={form.isDirty && !form.isValidTime}
              mandatory
            />
          </Box>
          <Box justifyContent="flexStart">
            {form.get('start_at').isDirty && form.get('end_at').isDirty && !form.isValidTime && (
              <Text size="200" color="radicalRed1000" capitalize>
                {i18n.t('shift_management.templates.weeks.errors.incomplete_shift')}
              </Text>
            )}
          </Box>
        </Box>

        <Text.Normal size="200" weight="medium" color="grey800">
          {i18n.t('shift_management.default_shift.form_labels.start_end_time').toLowerCase()}
        </Text.Normal>
      </Box>

      {!!plannedBreakForms?.length && (
        <PlannedBreaks
          plannedBreakForms={plannedBreakForms}
          validationParams={{ startAt, endAt }}
          onDelete={onDeleteBreak}
          onAddBreak={onAddBreak}
        />
      )}
    </Box>
  )
}

export default observer(ShiftsAndBreaksTimePickers)
